import { RenderStateCamera } from "@novorender/api";
import { useCallback, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { bottomToolbarActions, selectActiveTab, selectIsExpanded } from "components/toolbars/mobile/slice";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { hiddenActions, useDispatchHidden, useHidden } from "contexts/hidden";
import {
    highlightCollectionsActions,
    useDispatchHighlightCollections,
    useHighlightCollections,
} from "contexts/highlightCollections";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import {
    GroupStatus,
    InternalTemporaryGroup,
    objectGroupsActions,
    useDispatchObjectGroups,
    useLazyObjectGroups,
} from "contexts/objectGroups";
import { selectionBasketActions, useDispatchSelectionBasket, useSelectionBasket } from "contexts/selectionBasket";
import { coordinatesActions, selectCoordinates, selectCoordinatesEditSessionStartSnapshot } from "features/coordinates";
import { measureActions, selectMeasureEntities } from "features/measure/measureSlice";
import {
    clippingOutlineLaserActions,
    selectLaserEditSessionStartSnapshot,
    selectOutlineLasers,
} from "features/outlineLaser/outlineLaserSlice";
import { pointLineActions, selectPointLines } from "features/pointLine/pointLineSlice";
import { selectPointLineEditSessionStartSnapshot } from "features/pointLine/pointLineSlice";
import { useSetupVersionComparisonPropertyTree } from "features/propertyTree/hooks/useSetupVersionComparisonPropertyTree";
import {
    Picker,
    renderActions,
    selectClippingEditSessionStartSnapshot,
    selectClippingPlanes,
    selectDefaultVisibility,
    selectHomeViewpoint,
    selectInitialCamera,
    selectPicker,
} from "features/render";
import { cloneCameraState } from "features/render/hooks/useHandleCameraMoved";
import { CameraType } from "features/render/types";
import { loadScene } from "features/render/utils";
import { cloneClippingPlanes } from "features/sectionPlane/sectionPlaneUndoRedo";
import { undoRedoActions } from "features/undoRedo";
import { useSetViewpoint } from "features/viewpoints/useSetViewpoint";
import { useSceneId } from "hooks/useSceneId";
import { explorerActions } from "slices/explorer";
import { selectBackendProvider, selectIsVersionComparison, selectPipelineConfig } from "slices/explorer";
import { ProcoreToolMode, procoreUxActions, selectActiveToolMode } from "slices/procoreUx";
import { telemetry } from "telemetry";

export type ResetViewFn = (options?: {
    resetCamera?: boolean;
    resetVersionComparisonPropertyTree?: boolean;
    preservePicker?: boolean;
}) => Promise<void>;

export function useResetView(): ResetViewFn {
    const {
        state: { view, db, internalEventEmitter },
    } = useExplorerGlobals();
    const sceneId = useSceneId();
    const defaultCamera = useAppSelector(selectInitialCamera);
    const homeViewpoint = useAppSelector(selectHomeViewpoint);
    const backendProvider = useAppSelector(selectBackendProvider);
    const setViewpoint = useSetViewpoint();
    const setViewpointRef = useRef(setViewpoint);
    setViewpointRef.current = setViewpoint;
    const dispatch = useAppDispatch();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const objectGroups = useLazyObjectGroups();
    const dispatchObjectGroups = useDispatchObjectGroups();
    const setupVersionComparisonPropertyTree = useSetupVersionComparisonPropertyTree();
    const isVersionComparison = useAppSelector(selectIsVersionComparison);
    const highlightedObjects = useHighlighted();
    const hiddenObjects = useHidden();
    const selectionBasket = useSelectionBasket();
    const highlightCollections = useHighlightCollections();
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    // Only fetch pipelineConfig for Novorender backend (used in version comparison)
    const pipelineConfig = useAppSelector((state) =>
        backendProvider !== "procore" ? selectPipelineConfig(state) : undefined,
    );
    // Measurements
    const coordinates = useAppSelector(selectCoordinates);
    const lasers = useAppSelector(selectOutlineLasers);
    const pointLines = useAppSelector(selectPointLines);
    const dimensions = useAppSelector(selectMeasureEntities);
    // Section related
    const sectionPlanes = useAppSelector(selectClippingPlanes);
    const currentPicker = useAppSelector(selectPicker);
    // Procore tool-mode UI state (so undo restores the user's prior mode after a reset).
    const activeToolMode = useAppSelector(selectActiveToolMode);
    const bottomToolbarActiveTab = useAppSelector(selectActiveTab);
    const bottomToolbarIsExpanded = useAppSelector(selectIsExpanded);
    // Edit-session start snapshots cleared by resetView - capture so undo can restore them.
    const laserSnapshot = useAppSelector(selectLaserEditSessionStartSnapshot);
    const coordinatesSnapshot = useAppSelector(selectCoordinatesEditSessionStartSnapshot);
    const pointLineSnapshot = useAppSelector(selectPointLineEditSessionStartSnapshot);
    const clippingSnapshot = useAppSelector(selectClippingEditSessionStartSnapshot);

    const clearObjectHighlights = () => {
        dispatchHidden(hiddenActions.setIds([]));
        dispatchHighlighted(highlightActions.setIds([]));
        dispatchSelectionBasket(selectionBasketActions.set([]));
        dispatchHighlightCollections(highlightCollectionsActions.clearAll());
    };

    const lastSavedCameraState = useRef<RenderStateCamera | null>(null);

    const undoResetView = useCallback(() => {
        const dimensionsEntitiesToRestore = dimensions.map((set) => structuredClone(set));

        dispatchHidden(hiddenActions.setIds(hiddenObjects.idArr));
        dispatchHighlighted(highlightActions.setIds(highlightedObjects.idArr));
        dispatchSelectionBasket(selectionBasketActions.set(selectionBasket.idArr));
        dispatchHighlightCollections(highlightCollectionsActions.setAll(highlightCollections));
        dispatch(renderActions.setDefaultVisibility(defaultVisibility));
        dispatch(coordinatesActions.setCoordinates(coordinates));
        dispatch(clippingOutlineLaserActions.setLasers(lasers));
        dispatch(pointLineActions.setPointLines(pointLines));
        if (dimensionsEntitiesToRestore.length === 1) {
            dispatch(measureActions.setSelectedEntities(dimensionsEntitiesToRestore[0]));
        } else {
            dimensionsEntitiesToRestore.forEach((entities, i) =>
                dispatch(measureActions.setMeasureSet({ idx: i, entities })),
            );
        }
        const clonedSectionPlanes = cloneClippingPlanes(sectionPlanes.planes || []);
        dispatch(
            renderActions.setClippingPlanes(
                clonedSectionPlanes.length === 0
                    ? { planes: clonedSectionPlanes, enabled: false }
                    : { planes: clonedSectionPlanes, enabled: true },
            ),
        );

        if (lastSavedCameraState.current) {
            dispatch(
                renderActions.setCamera({
                    type:
                        lastSavedCameraState.current.kind === "pinhole" ? CameraType.Pinhole : CameraType.Orthographic,
                    goTo: cloneCameraState(lastSavedCameraState.current),
                }),
            );
        }

        // Restore edit-session snapshots so a subsequent confirm/cancel in the
        // restored tool mode can revert the in-session changes correctly.
        dispatch(clippingOutlineLaserActions.setEditSessionStartSnapshot(laserSnapshot));
        dispatch(coordinatesActions.setEditSessionStartSnapshot(coordinatesSnapshot));
        dispatch(pointLineActions.setEditSessionStartSnapshot(pointLineSnapshot));
        dispatch(renderActions.setClippingEditSessionStartSnapshot(clippingSnapshot));

        // Restore the Procore tool-mode UI: active tool mode + bottom-toolbar tab/expansion.
        dispatch(renderActions.setPicker(currentPicker));
        dispatch(procoreUxActions.setActiveToolMode(activeToolMode));
        dispatch(bottomToolbarActions.setActiveTab(bottomToolbarActiveTab));
        if (bottomToolbarIsExpanded) {
            dispatch(bottomToolbarActions.expand());
        } else {
            dispatch(bottomToolbarActions.collapse());
        }
    }, [
        dispatch,
        dispatchHidden,
        hiddenObjects.idArr,
        dispatchHighlighted,
        highlightedObjects.idArr,
        dispatchSelectionBasket,
        selectionBasket.idArr,
        dispatchHighlightCollections,
        highlightCollections,
        defaultVisibility,
        coordinates,
        lasers,
        pointLines,
        dimensions,
        sectionPlanes,
        currentPicker,
        activeToolMode,
        bottomToolbarActiveTab,
        bottomToolbarIsExpanded,
        laserSnapshot,
        coordinatesSnapshot,
        pointLineSnapshot,
        clippingSnapshot,
    ]);

    return async ({
        resetCamera = true,
        resetVersionComparisonPropertyTree = true,
        preservePicker = false,
    }:
        | {
              resetCamera?: boolean;
              resetVersionComparisonPropertyTree?: boolean;
              preservePicker?: boolean;
          }
        | undefined = {}) => {
        async function resetView() {
            try {
                // make reusable in case setting the home viewpoint fails and we need to fallback to a generic reset
                const procoreResetView = () => {
                    const initialCamera = resetCamera ? structuredClone(defaultCamera) : undefined;

                    const sceneData = {
                        title: "",
                        objectGroups: [],
                        settings: undefined,
                        customProperties: {},
                        projectId: "",
                    };

                    dispatch(renderActions.clearAllObjectColor());
                    dispatch(
                        renderActions.resetView({
                            sceneData,
                            initialCamera,
                        }),
                    );
                };

                // For Procore backend, don't call loadScene (Novorender API)
                // Scene data is already loaded via Procore APIs
                if (backendProvider === "procore") {
                    procoreResetView();
                } else {
                    // Novorender backend - use loadScene API
                    const [{ url: _url, db: _db, ...sceneData }, camera] = await loadScene(sceneId);
                    const groups = sceneData.objectGroups ?? [];
                    const initialCamera = resetCamera ? (camera ?? structuredClone(defaultCamera)) : undefined;

                    dispatch(renderActions.clearAllObjectColor());
                    dispatch(
                        renderActions.resetView({
                            sceneData,
                            initialCamera,
                        }),
                    );

                    dispatchObjectGroups(
                        objectGroupsActions.set(
                            objectGroups.current
                                .filter((group) => group.grouping !== InternalTemporaryGroup.BIMcollab)
                                .map((group) => {
                                    const saved = groups.find((grp) => group.id === grp.id);

                                    if (saved) {
                                        let status: GroupStatus;
                                        if (saved.selected) {
                                            status = GroupStatus.Selected;
                                        } else if (saved.hidden) {
                                            status = GroupStatus.Hidden;
                                        } else if (saved.frozen) {
                                            status = GroupStatus.Frozen;
                                        } else {
                                            status = GroupStatus.None;
                                        }
                                        return { ...group, status, opacity: saved.opacity ?? 0 };
                                    }

                                    return group;
                                })
                                .sort(
                                    (a, b) =>
                                        groups.findIndex((grp) => grp.id === a.id) -
                                        groups.findIndex((grp) => grp.id === b.id),
                                ),
                        ),
                    );
                }

                // Emit viewReset event through internal EventEmitter.
                // NOTE: This intentionally happens immediately after render.resetView has
                // completed, but before clearing clipping outlines, coordinates and object
                // highlights below. Consumers of the event rely on seeing the freshly
                // reset scene/camera state while any existing auxiliary UI state is still
                // present, so the ordering here is part of the event's contract.
                // In lib mode, WebviewerBridge will propagate this to the public webviewer.events API.
                if (internalEventEmitter) {
                    await internalEventEmitter.emit("viewReset", {
                        timestamp: Date.now(),
                        sceneId,
                        cameraReset: resetCamera,
                    });
                }

                dispatch(clippingOutlineLaserActions.clear());
                dispatch(clippingOutlineLaserActions.clearEditSessionStartSnapshot());
                dispatch(coordinatesActions.setHideCoordinateGizmo(false));
                dispatch(coordinatesActions.clear());
                dispatch(coordinatesActions.clearEditSessionStartSnapshot());
                dispatch(pointLineActions.clearEditSessionStartSnapshot());
                dispatch(renderActions.clearClippingEditSessionStartSnapshot());

                if (!preservePicker) {
                    dispatch(renderActions.setPicker(Picker.Object));
                    dispatch(procoreUxActions.setActiveToolMode(ProcoreToolMode.Default));
                }

                clearObjectHighlights();

                // Apply home viewpoint if available and we're resetting camera
                if (resetCamera && homeViewpoint && backendProvider === "procore") {
                    try {
                        await setViewpointRef.current(homeViewpoint, "system");
                    } catch (e) {
                        console.warn("Failed to apply home viewpoint:", e);
                        procoreResetView();
                    }
                }

                // Only restore object groups for Novorender backend
                // Procore doesn't have saved groups yet

                if (resetVersionComparisonPropertyTree && db && view?.renderState.scene?.url && isVersionComparison) {
                    void setupVersionComparisonPropertyTree(db, view, pipelineConfig?.version, backendProvider);
                }
            } catch (e) {
                console.error("Failed to reset view:", e);
                telemetry.trackCaughtException({
                    source: "home.useResetView.resetView",
                    category: "data",
                    error: e,
                    severity: "error",
                });
                dispatch(
                    explorerActions.setSnackbarMessage({
                        msg: "Failed to reset view. Please try again.",
                        closeAfter: 4000,
                    }),
                );
            }
        }
        dispatch(renderActions.setRecordCameraMoveUndoRedo(false));
        if (view?.renderState.camera) {
            lastSavedCameraState.current = cloneCameraState(view.renderState.camera);
        }
        await resetView();

        dispatch(
            undoRedoActions.push({
                description: "Reset View",
                undoCallback: async () => {
                    dispatch(renderActions.setRecordCameraMoveUndoRedo(false));
                    undoResetView();
                },
                redoCallback: async () => {
                    dispatch(renderActions.setRecordCameraMoveUndoRedo(false));
                    await resetView();
                },
            }),
        );
    };
}
